// Scene settings guide — the right-rail Settings panel shown for a scene when
// no element is selected (ViewToolbar), plus the course-wide Scene defaults in
// Global Config (SceneDefaultsSection). Locale-keyed like every guide
// (content.<lang> → { feature, title, sections }).

const sceneSettingsGuide = {
  id: "scene-settings",
  icon: "timer",
  keywords: [
    "scene settings",
    "sahne ayarları",
    "slide duration",
    "sahne süresi",
    "duration",
    "süre",
    "mandatory",
    "zorunlu",
    "zorunluluk",
    "wait for question",
    "soruyu bekle",
    "correct answer",
    "doğru cevap",
    "auto next",
    "otomatik ilerle",
    "countdown",
    "geri sayım",
    "transition",
    "geçiş",
    "scene defaults",
    "sahne varsayılanları",
    "next button",
    "ileri",
    "ajustes de escena",
    "duración de la escena",
    "obligatoria",
    "preceptivo",
    "respuesta correcta",
    "cambio automático",
    "cuenta atrás",
    "transición",
    "valores por defecto de escena",
    "siguiente",
  ],
  content: {
    en: {
      feature: "Scenes",
      title: "Scene settings",
      sections: [
        {
          id: "open",
          title: "Opening a scene's settings",
          videoUrl: null,
          text:
            "Click an empty part of the canvas so no element is selected. The right rail's **Settings** tab then shows the current scene's own settings:\n\n" +
            "- **General** — **Page name**, the mandatory and transition options below, and **Hide/Show Slide**.\n" +
            "- **Narration Sound** — the scene's narration audio.\n" +
            "- **Skip Interactive** — only on scenes with interactive scenario items; gives learners a Skip button during the interactive moment.",
        },
        {
          id: "mandatory",
          title: "Mandatory & slide duration",
          videoUrl: null,
          text:
            "When **Mandatory** is on, the learner's Next button stays locked until the scene's requirement is met. New scenes start as mandatory. Pick the requirement under **Slide Transition Options**:\n\n" +
            "- **Wait for Slide Duration** — always available.\n" +
            "- **Wait for Click** — offered only when the scene contains a Click element.\n" +
            "- **Wait for Question** — offered only when the scene contains a question; tick **Wait for Correct Answer** to require a correct answer.\n\n" +
            "With **Wait for Slide Duration**, a **Slide Duration** field (seconds) appears. Leave it empty to use the scene's own length — the grey placeholder shows that value.",
        },
        {
          id: "auto-advance",
          title: "Auto Next Slide & countdown",
          videoUrl: null,
          text:
            "**Auto Next Slide** moves the learner to the next scene on its own, once the whole scene has finished. It works independently of **Mandatory**.\n\n" +
            "With it on, **Show countdown before advancing** shows the next-scene card with a 5-second countdown first; with that off, the scene advances silently.\n\n" +
            "Scenes that contain questions never advance automatically, so the learner can read the answer feedback — the toggle is hidden on those scenes.",
        },
        {
          id: "end-card",
          title: "Scene-end transition card",
          videoUrl: null,
          text:
            "**Scene-end transition card** shows a card inviting the learner to the next scene once the scene finishes and its requirement (if any) is met. Off (the default), the scene simply stays on screen and the learner presses Next.\n\n" +
            "While **Auto Next Slide** is on, this option is hidden because the countdown setting controls the card instead. On question scenes it always stays available.",
        },
        {
          id: "defaults",
          title: "Defaults for every scene",
          videoUrl: null,
          text:
            "**⋮ → Global Config → Scene defaults** sets these options for the whole training:\n\n" +
            "- **Default scene duration (sec)** — used for scenes whose length can't be worked out from their content.\n" +
            "- **Standard scenes** — **Mandatory** and **Slide Duration** for scenes without questions.\n" +
            "- **Question scenes** — **Mandatory** with **Wait for Slide Duration** or **Wait for Question** (plus **Wait for Correct Answer**).\n" +
            "- **Scene transition** — **Automatic next scene** and the countdown or scene-end card.\n\n" +
            "**Important:** every time you press **Save changes** in Global Config, these defaults are written to all existing scenes (standard rules to scenes without questions, question rules to scenes with them), replacing per-scene choices. Fine-tune individual scenes afterwards. Scenes you add later inherit only the transition settings.",
        },
      ],
    },
    tr: {
      feature: "Sahneler",
      title: "Sahne ayarları",
      sections: [
        {
          id: "open",
          title: "Sahne ayarlarını açma",
          videoUrl: null,
          text:
            "Hiçbir öğe seçili olmayacak şekilde tuvalin boş bir yerine tıklayın. Sağ paneldeki **Ayarlar** sekmesi bu durumda geçerli sahnenin kendi ayarlarını gösterir:\n\n" +
            "- **Genel** — **Sayfa İsmi**, aşağıda anlatılan zorunluluk ve geçiş seçenekleri ve **Sahneyi Gizle/Göster**.\n" +
            "- **Anlatım Sesi** — sahnenin seslendirme sesi.\n" +
            "- **Etkileşimi Atla** — yalnızca etkileşimli senaryo öğesi içeren sahnelerde görünür; etkileşim sırasında öğrenciye bir Atla düğmesi sunar.",
        },
        {
          id: "mandatory",
          title: "Zorunluluk ve sahne süresi",
          videoUrl: null,
          text:
            "**Zorunluluk** açıkken öğrencinin İleri düğmesi, sahnenin koşulu sağlanana kadar kilitli kalır. Yeni sahneler zorunlu olarak başlar. Koşulu **Sahne Geçiş Seçenekleri** altından seçin:\n\n" +
            "- **Sahne Süresini Bekle** — her zaman kullanılabilir.\n" +
            "- **Tıklamayı Bekle** — yalnızca sahnede bir Tıklama öğesi varsa sunulur.\n" +
            "- **Cevabı Bekle** — yalnızca sahnede bir soru varsa sunulur; doğru cevap şartı için **Doğru Cevabı Bekle**'yi işaretleyin.\n\n" +
            "**Sahne Süresini Bekle** seçiliyken **Sahne Süresi** alanı (saniye) görünür. Boş bırakırsanız sahnenin kendi uzunluğu kullanılır — gri yer tutucu bu değeri gösterir.",
        },
        {
          id: "auto-advance",
          title: "Otomatik ilerleme ve geri sayım",
          videoUrl: null,
          text:
            "**Otomatik İlerle**, sahnenin tamamı bittikten sonra öğrenciyi kendiliğinden sonraki sahneye geçirir. **Zorunluluk** ayarından bağımsız çalışır.\n\n" +
            "Açıkken **Geçmeden önce geri sayım göster**, önce sonraki sahne kartını 5 saniyelik geri sayımla gösterir; bu seçenek kapalıysa geçiş doğrudan yapılır.\n\n" +
            "Soru içeren sahneler, öğrenci geri bildirimi okuyabilsin diye hiçbir zaman otomatik ilerlemez — bu sahnelerde seçenek gizlenir.",
        },
        {
          id: "end-card",
          title: "Sahne sonu geçiş ekranı",
          videoUrl: null,
          text:
            "**Sahne sonunda geçiş ekranı göster**, sahne bittiğinde ve varsa zorunluluk tamamlandığında öğrenciyi sonraki sahneye davet eden bir kart gösterir. Kapalıyken (varsayılan) sahne ekranda kalır; öğrenci İleri düğmesiyle devam eder.\n\n" +
            "**Otomatik İlerle** açıkken bu seçenek gizlenir, çünkü kartı geri sayım ayarı yönetir. Soru sahnelerinde ise her zaman kullanılabilir.",
        },
        {
          id: "defaults",
          title: "Tüm sahneler için varsayılanlar",
          videoUrl: null,
          text:
            "**⋮ → Genel Konfigürasyon → Sahne Varsayılanları** bu seçenekleri eğitimin tamamı için belirler:\n\n" +
            "- **Sahneler** — soru içermeyen sahneler için **Zorunlu** ve **Sahne Süresi** (timeline'daki sahne sonu süresi).\n" +
            "- **Soru sahneleri** — **Sahne Süresini Bekle** (timeline'daki sahne sonu süresi) veya **Cevabı Bekle** (ve **Doğru Cevabı Bekle**) ile **Zorunlu**.\n" +
            "- **Varsayılan sahne süresi (sn)** — her iki sekmede de Zorunlu kapalıyken görünür ve sekmeye özel ayrı bir değerdir: sahnenin tamamlandı sayılması için gereken süredir. Boş bırakılırsa sayfa başına minimum süre uygulanır.\n" +
            "- **Sahne geçişi** — **Otomatik sonraki sahne** ve geri sayım ya da sahne sonu kartı.\n\n" +
            "**Önemli:** Genel Konfigürasyon'da **Değişiklikleri Kaydet**'e her bastığınızda bu varsayılanlar mevcut tüm sahnelere yazılır (normal kurallar soru içermeyen sahnelere, soru kuralları soru içerenlere) ve sahne bazındaki seçimlerin yerini alır. Tek tek sahne ayarlarını bundan sonra yapın. Sonradan eklenen sahneler yalnızca geçiş ayarlarını devralır.",
        },
      ],
    },
    es: {
      feature: "Escenas",
      title: "Ajustes de la escena",
      sections: [
        {
          id: "open",
          title: "Abrir los ajustes de una escena",
          videoUrl: null,
          text:
            "Haz clic en una parte vacía del lienzo para que no haya ningún elemento seleccionado. La pestaña **Ajustes** del panel derecho muestra entonces los ajustes propios de la escena actual:\n\n" +
            "- **General** — **Nombre de la Página**, las opciones de obligatoriedad y de transición que se describen abajo, y **Ocultar/Mostrar Diapositiva**.\n" +
            "- **Sonido de Narración** — el audio de narración de la escena.\n" +
            "- **Omitir interactivo** — solo en escenas con elementos de escenario interactivo; ofrece a los alumnos un botón para saltar el momento interactivo.",
        },
        {
          id: "mandatory",
          title: "Obligatoriedad y duración de la escena",
          videoUrl: null,
          text:
            "Cuando **Obligatoria** está activado, el botón Siguiente del alumno permanece bloqueado hasta que se cumple el requisito de la escena. Las escenas nuevas empiezan como obligatorias. Elige el requisito en **Opciones de Transición de Diapositivas**:\n\n" +
            "- **Espere el Tiempo de Diapositiva** — siempre disponible.\n" +
            "- **Esperar el clic** — solo se ofrece cuando la escena contiene un elemento de clic.\n" +
            "- **Esperar la pregunta** — solo se ofrece cuando la escena contiene una pregunta; marca **Espera Respuesta Correcta** para exigir una respuesta correcta.\n\n" +
            "Con **Espere el Tiempo de Diapositiva**, aparece un campo **Duración de Escena** (en segundos). Déjalo vacío para usar la duración propia de la escena: el texto gris de ejemplo muestra ese valor.",
        },
        {
          id: "auto-advance",
          title: "Cambio automático y cuenta atrás",
          videoUrl: null,
          text:
            "**Cambio Automático** lleva al alumno a la siguiente escena por sí solo, una vez que ha terminado toda la escena. Funciona con independencia de **Obligatoria**.\n\n" +
            "Con él activado, **Mostrar cuenta atrás antes de avanzar** muestra primero la tarjeta de la siguiente escena con una cuenta atrás de 5 segundos; si está desactivado, la escena avanza sin aviso.\n\n" +
            "Las escenas que contienen preguntas nunca avanzan automáticamente, para que el alumno pueda leer la retroalimentación de su respuesta: en esas escenas el interruptor está oculto.",
        },
        {
          id: "end-card",
          title: "Tarjeta de transición al final de la escena",
          videoUrl: null,
          text:
            "**Tarjeta de transición al final de la escena** muestra una tarjeta que invita al alumno a pasar a la siguiente escena cuando la escena termina y se cumple su requisito (si lo hay). Si está desactivada (la opción predeterminada), la escena simplemente permanece en pantalla y el alumno pulsa Siguiente.\n\n" +
            "Mientras **Cambio Automático** está activado, esta opción se oculta porque el ajuste de la cuenta atrás controla la tarjeta. En las escenas con preguntas siempre está disponible.",
        },
        {
          id: "defaults",
          title: "Valores predeterminados para todas las escenas",
          videoUrl: null,
          text:
            "**⋮ → Configuración Global → Valores por defecto de escena** define estas opciones para toda la formación:\n\n" +
            "- **Duración predeterminada de escena (s)** — se usa en las escenas cuya duración no se puede calcular a partir de su contenido.\n" +
            "- **Escenas normales** — **Obligatoria** y **Duración de Escena** para las escenas sin preguntas.\n" +
            "- **Escenas con preguntas** — **Obligatoria** con **Espere el Tiempo de Diapositiva** o **Esperar la pregunta** (más **Espera Respuesta Correcta**).\n" +
            "- **Transición de escena** — **Siguiente escena automática** y la cuenta atrás o la tarjeta de final de escena.\n\n" +
            "**Importante:** cada vez que pulsas **Guardar Cambios** en Configuración Global, estos valores se escriben en todas las escenas existentes (las reglas normales en las escenas sin preguntas y las reglas de preguntas en las que las tienen) y sustituyen lo elegido en cada escena. Ajusta las escenas individuales después. Las escenas que añadas más tarde solo heredan los ajustes de transición.",
        },
      ],
    },
  },
};

export default sceneSettingsGuide;
